import * as React from 'react';
import { I18n } from '@aws-amplify/core';
import { SignIn } from 'aws-amplify-react';
// The styled Amplify UI primitives are not re-exported from the package root
// (the root exports the older, unstyled `amplify-*` variants), so they are
// imported from the same module the stock SignIn screen uses.
import {
  Button,
  FormField,
  FormSection,
  Hint,
  Input,
  InputLabel,
  Link,
  SectionBody,
  SectionFooter,
  SectionFooterPrimaryContent,
  SectionFooterSecondaryContent,
  SectionHeader,
} from 'aws-amplify-react/lib/Amplify-UI/Amplify-UI-Components-React';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';

// Deliberately no mailto here. This screen is public, so any address in the
// markup gets harvested by scrapers. The contact form reaches the same inbox
// without publishing it, and replying to an existing LabSmarts email is the
// fastest route for anyone who already has one.
const CONTACT_URL = 'https://labsmarts.com/contact';

/**
 * Drop-in replacement for the stock Amplify v3 SignIn screen that adds the
 * account guidance practitioners keep asking support about: that the username
 * is their email address, that it is case-sensitive, and what the password
 * policy is.
 *
 * Only `showComponent` is overridden — sign-in, MFA and error handling all
 * still come from the base class.
 */
export class LabSmartsSignIn extends SignIn {
  // Widened so the extra UI-only flags below can live alongside the base
  // class's `loading` state.
  public state: any;

  constructor(props: any) {
    super(props);

    this._validAuthStates = ['signIn', 'signedOut', 'signedUp'];
    this.state = { showPassword: false, capsLockOn: false };

    this.handleUsernameChange = this.handleUsernameChange.bind(this);
    this.handleCapsLock = this.handleCapsLock.bind(this);
    this.clearCapsLock = this.clearCapsLock.bind(this);
    this.toggleShowPassword = this.toggleShowPassword.bind(this);
  }

  // Email addresses never have leading/trailing spaces, and pasting one out of
  // an email client routinely picks them up. Trim on the way in so the paste
  // does not turn into an "Incorrect username or password" error.
  handleUsernameChange(event: React.ChangeEvent<HTMLInputElement>) {
    this.inputs = this.inputs || {};
    this.inputs.username = (event.target.value || '').trim();
  }

  handleCapsLock(event: React.KeyboardEvent<HTMLInputElement>) {
    if (typeof event.getModifierState !== 'function') {
      return;
    }
    const capsLockOn = event.getModifierState('CapsLock');
    if (capsLockOn !== this.state.capsLockOn) {
      this.setState({ capsLockOn } as any);
    }
  }

  clearCapsLock() {
    if (this.state.capsLockOn) {
      this.setState({ capsLockOn: false } as any);
    }
  }

  toggleShowPassword() {
    this.setState({ showPassword: !this.state.showPassword } as any);
  }

  showComponent(theme: any) {
    const { showPassword, capsLockOn } = this.state;

    return (
      <FormSection theme={theme} data-test="sign-in-section">
        <SectionHeader theme={theme} data-test="sign-in-header-section">
          {I18n.get('Sign in to your account')}
        </SectionHeader>
        <form onSubmit={this.signIn}>
          <SectionBody theme={theme}>
            <FormField theme={theme}>
              <InputLabel theme={theme}>Username (your email address)</InputLabel>
              <Input
                autoFocus
                defaultValue={this.state.username}
                placeholder="e.g. example@example.com"
                theme={theme}
                key="username"
                name="username"
                autoComplete="username"
                onChange={this.handleUsernameChange}
                data-test="username-input"
              />
              <div className="ls-auth-hint">
                The username is the email address used to create the account, and it is{' '}
                <strong>case-sensitive</strong> — enter it exactly as it was typed at sign-up.
              </div>
            </FormField>

            <FormField theme={theme}>
              <div className="ls-auth-label-row">
                <InputLabel theme={theme}>{I18n.get('Password')}</InputLabel>
                <button
                  type="button"
                  className="ls-auth-toggle"
                  onClick={this.toggleShowPassword}
                  data-test="sign-in-show-password-toggle"
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />{' '}
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <Input
                placeholder={I18n.get('Enter your password')}
                theme={theme}
                key="password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                onChange={this.handleInputChange}
                onKeyDown={this.handleCapsLock}
                onKeyUp={this.handleCapsLock}
                onBlur={this.clearCapsLock}
                data-test="sign-in-password-input"
              />
              {capsLockOn && (
                <div className="ls-auth-capslock" data-test="sign-in-caps-lock-warning">
                  <FontAwesomeIcon icon={faExclamationTriangle} /> Caps Lock is on — passwords are
                  case-sensitive.
                </div>
              )}
              <Hint theme={theme}>
                {I18n.get('Forget your password? ')}
                <Link
                  theme={theme}
                  onClick={() => this.changeState('forgotPassword')}
                  data-test="sign-in-forgot-password-link"
                >
                  {I18n.get('Reset password')}
                </Link>
              </Hint>
            </FormField>
          </SectionBody>

          <SectionFooter theme={theme} data-test="sign-in-footer-section">
            <SectionFooterPrimaryContent theme={theme}>
              <Button
                theme={theme}
                type="submit"
                disabled={this.state.loading}
                data-test="sign-in-sign-in-button"
              >
                {I18n.get('Sign In')}
              </Button>
            </SectionFooterPrimaryContent>
            <SectionFooterSecondaryContent theme={theme}>
              {I18n.get('No account? ')}
              <Link
                theme={theme}
                onClick={() => this.changeState('signUp')}
                data-test="sign-in-create-account-link"
              >
                {I18n.get('Create account')}
              </Link>
            </SectionFooterSecondaryContent>
          </SectionFooter>
        </form>

        <div className="ls-auth-footnote">
          <div>LabSmarts is optimized for the Chrome and Brave browsers.</div>
          <div>
            Trouble signing in? Reach out to us using the{' '}
            <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
              contact form
            </a>
            .
          </div>
        </div>
      </FormSection>
    );
  }
}
